import { Button, Checkbox } from '@cimplico/components';
import { useEffect, useMemo, useState } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { OnboardingFormData } from './FYIGroupsOnboarding';
import DaySelect from '../../../../../../../components/day-select/DaySelect';
import MonthSelect from '../../../../../../../components/month-select/MonthSelect';

type GlobalControlsProps = {
    search?: string;
    showOnboarded: boolean;
    onExpand: (expand: boolean) => void;
};

function GlobalControls({ showOnboarded, search = '', onExpand }: GlobalControlsProps): JSX.Element {
    const [expanded, setExpanded] = useState(false);
    const [month, setMonth] = useState<number>();
    const [day, setDay] = useState<number>();

    const { setValue, getValues, control } = useFormContext<OnboardingFormData>();
    const { field: selectField } = useController({ name: `globalSelected`, defaultValue: false });

    const data = useWatch({ control, name: 'data' });

    const filteredData = useMemo(() => {
        return data
            .filter((d) => !d.group.fyiGroup.existingMapping)
            .filter((d) => d.group.fyiGroup.name.toLowerCase().includes(search.toLowerCase()));
    }, [search, data, showOnboarded]);

    function handleExpand(): void {
        setExpanded(!expanded);
        onExpand(!expanded);

        setValue(
            `data`,
            getValues(`data`).map((d) => {
                if (filteredData.find((data) => data.group.fyiGroup.id === d.group.fyiGroup.id)) {
                    return {
                        ...d,
                        group: { ...d.group, isExpanded: !expanded },
                    };
                }
                return d;
            })
        );
    }

    function handleSelect(checked: boolean): void {
        selectField.onChange(checked);
        setValue(
            `data`,
            getValues(`data`).map((d) => {
                if (filteredData.find((data) => data.group.fyiGroup.id === d.group.fyiGroup.id)) {
                    return {
                        group: { ...d.group, isSelected: checked, monthError: undefined, dayError: undefined },
                        entities: d.entities.map((e) => ({ ...e, isSelected: checked, monthError: undefined, dayError: undefined })),
                    };
                }
                return d;
            })
        );
    }

    function handleMonth(month: number): void {
        setMonth(month);
        setDay(undefined);
        setValue(
            `data`,
            getValues(`data`).map((d) => {
                if (filteredData.find((data) => data.group.fyiGroup.id === d.group.fyiGroup.id)) {
                    return {
                        group: { ...d.group, month, day: undefined, monthError: undefined, dayError: undefined },
                        entities: d.entities.map((e) => ({ ...e, month, day: undefined, monthError: undefined, dayError: undefined })),
                    };
                }
                return d;
            })
        );
    }

    function handleDay(day: number): void {
        setDay(day);
        setValue(
            `data`,
            getValues(`data`).map((d) => {
                if (filteredData.find((data) => data.group.fyiGroup.id === d.group.fyiGroup.id)) {
                    return {
                        group: { ...d.group, day, monthError: undefined, dayError: undefined },
                        entities: d.entities.map((e) => ({ ...e, day, monthError: undefined, dayError: undefined })),
                    };
                }
                return d;
            })
        );
    }

    useEffect(() => {
        if (selectField.value && !filteredData.every((data) => data.group.isSelected)) {
            selectField.onChange(false);
        } else if (!selectField.value && filteredData.every((data) => data.group.isSelected)) {
            selectField.onChange(true);
        }
    }, [filteredData]);

    useEffect(() => {
        setMonth(undefined);
        setDay(undefined);
    }, [search]);

    return (
        <div className="flex items-center justify-between mt-4">
            <div className="flex gap-4">
                {/* <Button size="sm" type="secondary" label={expanded ? 'Collapse All' : 'Expand All'} onClick={handleExpand} /> */}
                <Checkbox
                    label={`Select All (${filteredData.filter((d) => d.group.isSelected).length} / ${filteredData.length} selected)`}
                    checked={selectField.value}
                    onChange={(e): void => handleSelect(e.target.checked)}
                    value={selectField.value}
                />
            </div>
            <div className="flex gap-2 pr-2">
                <div className="w-[150px]">
                    <MonthSelect onChange={(m): void => handleMonth(m[0])} defaultValue={month} size="small" />
                </div>
                <div className="w-[150px]">
                    <DaySelect onChange={(d): void => handleDay(d[0])} selectedMonth={month} defaultValue={day} size="small" />
                </div>
            </div>
        </div>
    );
}

export default GlobalControls;
